import {
  cloneElement,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { CHECKBOX, CHROME, CONTROL_NUMERIC, FIELD_EXTRA, FIELDSET, RADIO } from './registry';

/**
 * Data entry: text inputs, selects, textareas and checkboxes, plus the label that names them.
 *
 * `id` is required rather than optional on every control here. A field whose label is not
 * programmatically associated is a field a screen reader announces as "edit, blank", and
 * making the association optional is how that happens — twenty-nine raw inputs in this app
 * before the primitives existed, and the association was made by hand each time.
 *
 * None of these accepts `className`. See the note in `Button.tsx`. Where a call site needs
 * the control to fill its row, that is `grow`, a layout question the primitive answers —
 * not a utility string the caller appends.
 */

/**
 * The id `Field` gives its hint, so a caller wiring `aria-describedby` reads it from here
 * rather than retyping the convention. The literal appeared at one call site and matched by
 * luck; changing the convention there would have detached the description silently, with a
 * green build and a control that no longer explains itself to a screen reader.
 */
export const hintId = (id: string): string => `${id}-hint`;

interface FieldShellProps {
  id: string;
  label: string;
  /** Hide the label visually but keep it for assistive technology. */
  labelHidden?: boolean;
  /**
   * A short marker beside the label, stating something about the VALUE the control cannot.
   *
   * Two states earn it: `OFF` for a setting deliberately disabled by an in-band value, and
   * `NOT A NUMBER` for a box whose contents could not be read. Deliberately NOT an `INHERITED`
   * state — `hint` already says "Inherited: 7" in words, and a badge on every inherited field
   * is a marker on most of the form, which is a marker on none of it.
   */
  badge?: string | undefined;
  /**
   * A control acting on this one field — "Reset to inherited". Takes a node rather than a
   * handler so the caller uses a real `Button` from this layer.
   */
  action?: ReactNode;
  /**
   * Shown under the control. `Field` wires it to the control itself — never retype
   * `aria-describedby` at a call site, which is exactly how three of them forgot it.
   */
  hint?: ReactNode;
  /**
   * States that the control's value is mandatory. Renders a visible "(required)" after the
   * label text, and sets `aria-required` and the native `required` on the single child
   * control.
   */
  required?: boolean;
  children: ReactNode;
}

/**
 * Merges a hint's id into whatever `aria-describedby` the child already carries, rather than
 * overwriting it. A call site that already wired the hint by hand (the convention predates
 * this function) would otherwise end up with the id doubled or, worse, silently dropped.
 */
function mergeDescribedBy(existing: string | undefined, hintIdValue: string): string {
  if (existing === undefined || existing === '') return hintIdValue;
  const tokens = existing.split(/\s+/).filter(Boolean);
  return tokens.includes(hintIdValue) ? existing : `${existing} ${hintIdValue}`;
}

/**
 * Clones the field's single child control so `aria-describedby` and `required` cannot be
 * forgotten at the call site — the failure mode this replaces. `isValidElement` is the guard
 * for the (currently unused) case of `children` being something other than one element; the
 * field simply renders it unwired rather than throwing.
 */
function wireControl(
  children: ReactNode,
  id: string,
  hint: ReactNode,
  required: boolean,
): ReactNode {
  if (!isValidElement(children)) return children;
  const child = children as ReactElement<Record<string, unknown>>;
  const existingDescribedBy = child.props['aria-describedby'] as string | undefined;
  const describedBy = hint
    ? mergeDescribedBy(existingDescribedBy, hintId(id))
    : existingDescribedBy;
  return cloneElement(child, {
    ...(describedBy === undefined ? {} : { 'aria-describedby': describedBy }),
    ...(required ? { 'aria-required': true, required: true } : {}),
  });
}

/**
 * Related fields under one legend.
 *
 * A real `fieldset`, because that is the element a screen reader announces the legend with —
 * a `div` with a heading reads the heading once and then leaves every field inside it
 * unattached to the thing that groups them.
 *
 * `summary` is a derived sentence about the SET rather than any field in it: "Nudge 3 fires
 * first, on day 3". It sits under the legend rather than at the end, because it is the answer
 * to what the group does and a reader wants that before the inputs, not after them.
 */
export function FieldGroup({
  legend,
  description,
  summary,
  row = false,
  children,
}: {
  legend: string;
  description?: ReactNode;
  summary?: ReactNode;
  /** Lay the fields on one line. For two- and three-character values that would else stack. */
  row?: boolean;
  children: ReactNode;
}) {
  return (
    <fieldset className={FIELDSET.group}>
      <legend className={FIELDSET.legend}>{legend}</legend>
      {description === undefined ? null : <p className={FIELDSET.description}>{description}</p>}
      {summary === undefined ? null : (
        // Announced, because it changes as the fields beside it are edited and that change is
        // the reason it exists.
        <p className={FIELDSET.summary} role="status" aria-live="polite">
          {summary}
        </p>
      )}
      <div className={row ? FIELDSET.row : undefined}>{children}</div>
    </fieldset>
  );
}

export function Field({
  id,
  label,
  labelHidden = false,
  hint,
  badge,
  action,
  required = false,
  children,
}: FieldShellProps) {
  return (
    <div className="fld">
      {/* The label row, not just the label. `badge` and `action` belong beside the name rather
          than under the control: a marker below the box reads as part of the value, and a
          reset button below it reads as a form submit. */}
      <div className={badge === undefined && action === undefined ? undefined : FIELD_EXTRA.action}>
        <label className={labelHidden ? 'vh' : undefined} htmlFor={id}>
          {label}
          {/* Plain text, not a badge — a required marker names an obligation on every reader
              of the form, not a fact about this one field's current value. */}
          {required ? ' (required)' : null}
        </label>
        {badge === undefined ? null : <span className={FIELD_EXTRA.badge}>{badge}</span>}
        {action}
      </div>
      {wireControl(children, id, hint, required)}
      {hint ? (
        <span className="hint" id={hintId(id)}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'>;

export interface TextInputProps extends NativeInput {
  id: string;
  /** Fill the remaining width of the row it sits in. */
  grow?: boolean;
  /**
   * Tabular figures, for a control holding a number. Proportional digits change width as
   * they are typed, so an hours field appears to shift while the reader edits it.
   */
  numeric?: boolean;
  /**
   * The element, for the two things React state cannot reach: moving focus, and clearing a
   * file input after a successful submit — `<input type="file">` has no controllable value,
   * so without this the picked file stays named on screen after it has been sent.
   */
  ref?: Ref<HTMLInputElement>;
  /**
   * A box holding two or three characters — an hour, a number of days. A boolean mirroring
   * `grow` rather than a width union, so the primitive keeps one prop shape for "how wide".
   */
  short?: boolean;
  /**
   * The value could not be read. Sets `aria-invalid` as well as the border, because colour is
   * never the only signal and the border is dropped under forced colours.
   */
  invalid?: boolean;
}

export function TextInput({
  id,
  grow = false,
  short = false,
  numeric = false,
  invalid = false,
  ...rest
}: TextInputProps) {
  const classes: string[] = [CHROME.field];
  if (grow) classes.push('ctl-grow');
  if (short) classes.push(CHROME.short);
  if (numeric) classes.push(CONTROL_NUMERIC);
  if (invalid) classes.push(CHROME.invalid);
  // `aria-invalid` alongside the class, never the class alone: WCAG 1.4.1, and under
  // forced-colors the border tint is dropped entirely.
  return (
    <input {...rest} id={id} aria-invalid={invalid || undefined} className={classes.join(' ')} />
  );
}

type NativeSelect = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'id'>;

export interface SelectProps extends NativeSelect {
  id: string;
  children: ReactNode;
  grow?: boolean;
  /** Two digits wide. Same class and same reason as `TextInput`'s. */
  short?: boolean;
}

export function Select({ id, grow = false, short = false, children, ...rest }: SelectProps) {
  const classes: string[] = [CHROME.field];
  if (grow) classes.push('ctl-grow');
  if (short) classes.push(CHROME.short);
  return (
    <select {...rest} id={id} className={classes.join(' ')}>
      {children}
    </select>
  );
}

/**
 * `type` IS OMITTED AS WELL AS `className`, which `TextInputProps` does not do.
 *
 * That omission is the whole reason this is a primitive rather than `<TextInput type="date">`.
 * The latter type-checks today and spreads straight onto the input, so a date control could be
 * written at a call site with no width class, nowhere to hang the `min`/`max` convention, and
 * nothing anywhere recording that a date control exists. Omitting `type` also means nobody can
 * turn a `DateInput` back into a text box from the outside.
 */
type NativeDate = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id' | 'type'>;

export interface DateInputProps extends NativeDate {
  id: string;
  /** `YYYY-MM-DD`, which is what the control reads and writes in every locale. */
  value: string;
  /**
   * The value could not be used. Sets `aria-invalid` as well as the border, because colour is
   * never the only signal and the border is dropped under forced colours.
   */
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/**
 * A calendar day.
 *
 * DELIBERATELY DUMB: a `YYYY-MM-DD` string in and the same out. A date means different instants
 * in different places, and which zone a given date belongs to is a decision about the thing
 * being edited — a tenant's configured zone, never the reader's browser. A primitive that
 * guessed would guess wrong for every caller that needed the other answer.
 *
 * There is no `grow`. A date has a fixed content width, and `grow`/`short`/`date` would be three
 * answers to one question.
 */
export function DateInput({ id, invalid = false, ...rest }: DateInputProps) {
  const classes: string[] = [CHROME.field, CHROME.date];
  if (invalid) classes.push(CHROME.invalid);
  return (
    <input
      {...rest}
      type="date"
      id={id}
      aria-invalid={invalid || undefined}
      className={classes.join(' ')}
    />
  );
}

type NativeTextarea = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'id'>;

export interface TextAreaProps extends NativeTextarea {
  id: string;
}

export function TextArea({ id, ...rest }: TextAreaProps) {
  return <textarea {...rest} id={id} className={CHROME.field} />;
}

export interface CheckboxProps extends Omit<NativeInput, 'type'> {
  id: string;
  /**
   * The sentence beside the box. Carried by the primitive rather than left to the caller
   * because a checkbox whose label is only adjacent text is a 18px target with no name —
   * clicking the words has to toggle it, and a screen reader has to read them.
   */
  label: ReactNode;
}

export function Checkbox({ id, label, ...rest }: CheckboxProps) {
  return (
    <span className={CHECKBOX.row}>
      <input {...rest} type="checkbox" id={id} className={CHECKBOX.box} />
      <label htmlFor={id}>{label}</label>
    </span>
  );
}

export interface RadioCardProps extends Omit<NativeInput, 'type'> {
  id: string;
  /**
   * The group this option belongs to. Required, not optional: radios that do not share a
   * name are independent controls, so every one of them stays selected and the reader is
   * given a set of choices that never excludes anything.
   */
  name: string;
  label: ReactNode;
  /** What picking this one means. Shown under the label rather than on hover. */
  description?: ReactNode;
}

/**
 * One radio, rendered as a card.
 *
 * The `label` wraps the input, so the whole card — 56px tall, full width — is the target and
 * the accessible name, rather than the 18px dot. `htmlFor` is set as well as the wrapping,
 * because Safari's VoiceOver reads the implicit association inconsistently.
 */
export function RadioCard({ id, name, label, description, ...rest }: RadioCardProps) {
  return (
    <label className={RADIO.card} htmlFor={id}>
      <input {...rest} type="radio" id={id} name={name} className={CHECKBOX.box} />
      <span>
        <span className={RADIO.label}>{label}</span>
        {description === undefined ? null : (
          <span className={RADIO.description}>{description}</span>
        )}
      </span>
    </label>
  );
}

/**
 * The set of them. A `fieldset` for the same reason `FilterBar` is one: without it a screen
 * reader announces the options as loose controls with no idea what question they answer.
 * The element's own border, padding and intrinsic minimum width are reset here — that
 * minimum is what makes a fieldset refuse to shrink inside a dialog.
 */
export function RadioCardGroup({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="fl">{legend}</legend>
      <div className={RADIO.group}>{children}</div>
    </fieldset>
  );
}
